// ═══════════════════════════════════════════════════════════════
// Handoff — Action Items
// Full CRUD with filters, priorities, deadlines, live countdowns
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import {
  Title, Text, Paper, Group, Stack, Badge, Button, TextInput, Select,
  Modal, Textarea,
} from '../../components/gab/GabComponents';
import { IconPlus, IconSearch, IconClock } from '@tabler/icons-react';
import { useHandoffStore } from '../../store/handoffStore';
import { useAuthStore } from '../../store/authStore';
import { PRIORITY_META } from '../../types';
import type { ActionPriority } from '../../types';
import { secondary } from '../../lib/colors';

function timeUntil(deadline: string): { text: string; overdue: boolean } {
  const now = Date.now();
  const dl = new Date(deadline).getTime();
  const diff = dl - now;
  if (diff < 0) return { text: 'OVERDUE', overdue: true };
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  if (days > 0) return { text: `${days}d ${hours}h`, overdue: false };
  return { text: `${hours}h`, overdue: false };
}

const FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Active/Pending' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'completed', label: 'Completed' },
];

export default function ActionItems() {
  const { actionItems, events: handoffEvents, rosterUsers, loadActionItems, loadRosterUsers, createActionItem, completeActionItem, reopenActionItem, deleteActionItem, fetchEvents } = useHandoffStore();
  const { incidents, loadIncidents } = useAuthStore();
  const PAGE_SIZE = 25;
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('pending');
  const [eventFilter, setEventFilter] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [priority, setPriority] = useState<ActionPriority>('normal');
  const [assignee, setAssignee] = useState('');
  const [deadline, setDeadline] = useState('');
  const [eventId, setEventId] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadActionItems(); loadIncidents(); loadRosterUsers(); fetchEvents(); }, []);

  const eventOptions = [
    { value: 'all', label: 'All Events' },
    ...handoffEvents.map(e => ({ value: String(e.id), label: e.eventName || `Event #${e.id}` })),
  ];

  const now = Date.now();
  const filtered = actionItems
    .filter(a => {
      if (filter === 'pending') return a.status === 'pending';
      if (filter === 'overdue') return a.status === 'pending' && a.dueDate && new Date(a.dueDate).getTime() < now;
      if (filter === 'completed') return a.status === 'completed';
      return true;
    })
    .filter(a => eventFilter === 'all' || String(a.eventId) === eventFilter)
    .filter(a => !search || (a.title || '').toLowerCase().includes(search.toLowerCase()) || (a.assignedTo || '').toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      if (a.status !== b.status) return a.status === 'pending' ? -1 : 1;
      const pOrder: Record<string, number> = { critical: 0, high: 1, normal: 2, low: 3 };
      return (pOrder[a.priority] ?? 2) - (pOrder[b.priority] ?? 2);
    });

  useEffect(() => { setPage(1); }, [search, filter, eventFilter]);
  // Pagination
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleCreate = async () => {
    if (!title.trim() || !eventId) return;
    setSaving(true);
    await createActionItem({
      eventId, title: title.trim(), description: desc.trim(),
      priority, assignedTo: assignee, dueDate: deadline,
    });
    setCreateOpen(false);
    setTitle(''); setDesc(''); setPriority('normal'); setAssignee(''); setDeadline(''); setEventId('');
    setSaving(false);
  };

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Action Items</Title>
        <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => setCreateOpen(true)}>New Action</Button>
      </Group>

      <Group mb="md" gap="sm">
        <TextInput placeholder="Search..." leftSection={<IconSearch size={14} />}
          value={search} onChange={e => setSearch(e.target.value)} size="xs" w={200} />
        <Select data={FILTER_OPTIONS} value={filter} onChange={v => setFilter(v ?? 'pending')} size="xs" w={160} />
        <Select data={eventOptions} value={eventFilter} onChange={v => setEventFilter(v ?? 'all')} size="xs" w={220} />
      </Group>

      {filtered.length === 0 ? (
        <Paper p="xl" radius="sm" withBorder>
          <Text ta="center" c="dimmed">No action items found.</Text>
        </Paper>
      ) : (
        <Stack gap="xs">
          {pageRows.map(a => {
            const hEvt = handoffEvents.find(e => e.id === Number(a.eventId));
            const inc = hEvt?.incidentId != null ? incidents.find(i => i.id === Number(hEvt.incidentId)) : undefined;
            const evt = { name: inc?.name || hEvt?.eventName || `Event #${a.eventId}` };
            const pm = PRIORITY_META[a.priority as keyof typeof PRIORITY_META] ?? PRIORITY_META.normal;
            const isOverdue = a.status === 'pending' && a.dueDate && new Date(a.dueDate).getTime() < now;
            const countdown = a.dueDate ? timeUntil(a.dueDate) : null;
            return (
              <Paper key={a.id} p="sm" radius="sm" withBorder
                style={{ borderLeft: `3px solid ${isOverdue ? 'var(--color-error)' : pm.color}`, opacity: a.status === 'completed' ? 0.6 : 1 }}>
                <Group justify="space-between" wrap="nowrap">
                  <div style={{ flex: 1 }}>
                    <Group gap="xs" mb={2}>
                      <Text size="sm" fw={500} td={a.status === 'completed' ? 'line-through' : undefined}>{a.title}</Text>
                      <Badge size="xs" style={{ background: pm.color + '15', color: pm.color }}>{pm.label}</Badge>
                      {isOverdue && <Badge size="xs" color="var(--color-error)">Overdue</Badge>}
                      {a.status === 'completed' && <Badge size="xs" color="var(--color-success)">Done</Badge>}
                    </Group>
                    {a.description && <Text size="xs" c="dimmed" lineClamp={1} mb={2}>{a.description}</Text>}
                    <Group gap="md">
                      <Text size="xs" c="dimmed">{evt?.name}</Text>
                      {a.assignedTo && <Text size="xs" c="dimmed">Assigned: {a.assignedTo}</Text>}
                      {countdown && (
                        <Group gap={4}>
                          <IconClock size={10} color={countdown.overdue ? 'var(--color-error)' : secondary[700]} />
                          <Text size="xs" c={countdown.overdue ? 'var(--color-error)' : 'dimmed'} fw={countdown.overdue ? 600 : 400}>
                            {countdown.text}
                          </Text>
                        </Group>
                      )}
                      {a.status === 'completed' && (a as any).completedByName && (
                        <Text size="xs" c="dimmed">Completed by {(a as any).completedByName}</Text>
                      )}
                    </Group>
                  </div>
                  <Group gap="xs">
                    {a.status === 'pending' ? (
                      <Button variant="subtle" size="xs" color="var(--color-success)" onClick={() => completeActionItem(a.id)}>
                        Complete
                      </Button>
                    ) : (
                      <Button variant="subtle" size="xs" onClick={() => reopenActionItem(a.id)}>
                        Reopen
                      </Button>
                    )}
                    <Button variant="subtle" size="xs" color="var(--color-error)" onClick={() => {
                      if (confirm('Delete this action item?')) deleteActionItem(a.id);
                    }}>Delete</Button>
                  </Group>
                </Group>
              </Paper>
            );
          })}
        </Stack>
      )}

      {totalPages > 1 && (
        <Group justify="center" mt="sm" gap="xs">
          <Button variant="subtle" size="xs" disabled={page === 1} onClick={() => setPage(1)}>««</Button>
          <Button variant="subtle" size="xs" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹ Prev</Button>
          {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
            const p = totalPages <= 7 ? i + 1 : page <= 4 ? i + 1 : page >= totalPages - 3 ? totalPages - 6 + i : page - 3 + i;
            return <Button key={p} variant={p === page ? 'filled' : 'subtle'} color="var(--color-primary-700)" size="xs" onClick={() => setPage(p)}>{p}</Button>;
          })}
          <Button variant="subtle" size="xs" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next ›</Button>
          <Button variant="subtle" size="xs" disabled={page === totalPages} onClick={() => setPage(totalPages)}>»»</Button>
        </Group>
      )}

      {/* Create Modal */}
      <Modal opened={createOpen} onClose={() => setCreateOpen(false)} title="New Action Item" size="md">
        <Stack gap="sm">
          <Select label="Event" placeholder="Select event"
            data={incidents.filter(e => e.id != null).map(e => ({ value: String(e.id), label: String(e.name || `Incident #${e.id}`) }))}
            value={eventId} onChange={v => setEventId(v ?? '')} required />
          <TextInput label="Title" value={title} onChange={e => setTitle(e.target.value)} required />
          <Textarea label="Description" value={desc} onChange={e => setDesc(e.target.value)} minRows={2} />
          <Group grow>
            <Select label="Priority"
              data={Object.entries(PRIORITY_META).map(([v, m]) => ({ value: v, label: m.label }))}
              value={priority} onChange={v => setPriority((v as ActionPriority) ?? 'normal')} />
            <Select label="Assignee" placeholder="Select person..."
              data={rosterUsers
                .filter((u: any) => u.fullName || u.name || u.email)
                .map((u: any) => ({
                  value: String(u.fullName || u.name || u.email || ''),
                  label: String(u.fullName || u.name || u.email || '(Unknown)'),
                }))}
              value={assignee || null} onChange={v => setAssignee(v ?? '')}
              searchable clearable />
          </Group>
          <TextInput label="Deadline" type="date" value={deadline}
            onChange={e => setDeadline(e.target.value)} />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} loading={saving} disabled={!title.trim() || !eventId}>Create</Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
